import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { MaterialDescriptions, MaterialDisclaimer, MaterialLabels } from '@alliance-disposal/transport-types';
import { FloatingPortal } from '@floating-ui/react';
import { InfoIcon } from 'lucide-react';
import { iconsForMaterial } from '../../utils/icons-for-materials';
import { ContentButton } from './content-button';
const infoIconDialogs = {
    cND: 'Excludes: Dirt, hazardous materials, more than 3 cubic yards of concrete or aggregates.',
    asbestos: 'Excludes: Trash and other debris.',
    asbestosNonFriable: 'Excludes: Trash and other debris.',
    asphalt: 'Excludes: Asphalt Trash and other debris.',
    bNB: 'Excludes: Dirt, debris, bamboo, or other trash.',
    bNL: 'Excludes: Dirt, debris, or other trash.',
    bNS: 'Excludes: Dirt, debris, or other trash.',
    cardboard: 'Excludes: Trash, other debris, etc.',
    concrete: 'Excludes: Concrete slurry, trash, dirt, asphalt, etc.',
    comix: 'Excludes: Concrete slurry, trash, dirt, asphalt, etc.',
    compost: 'Excludes: Trash, other debris, etc.',
    concreteRebar: 'Excludes: Concrete slurry, trash, dirt, asphalt, etc.',
    creosote: 'Excludes: Trash and other debris.',
    dirt: 'Excludes: Rocks, gravel, trash, other debris, etc.',
    sand: 'Excludes: Rocks, gravel, trash, other debris, etc.',
    scrap: 'Excludes: Trash, dirt, concrete, other debris, etc.',
    singleStream: 'Excludes: Trash and other debris.',
    roofing: 'Excludes: Trash and other debris.',
    commingle: 'Excludes: Trash and other debris.',
    WOOD_ROOFING: 'Excludes: Trash and other debris.',
    foodWaste: 'Excludes: Other debris.',
    mattresses: 'Excludes: Other debris.',
    SLATE_ROOFING: 'Excludes: Trash and other debris.',
    CONCRETE_ROOFING: 'Excludes: Trash and other debris.',
    tires: 'Excludes: Other debris.',
    dryIndustrialWaste: 'Excludes: Other debris.',
    bioSolids: 'Excludes: Other debris.',
    dust: 'Excludes: Other debris.',
};
export const MaterialCard = ({ option, onSelect }) => {
    const [showInfoMessage, setShowInfoMessage] = useState(false);
    const infoIconDialog = useMemo(() => infoIconDialogs[option.material], [option.material]);
    const anchorRef = useRef(null);
    const infoMessageRef = useRef(null);
    useEffect(() => {
        /**
         * Handles the click outside of the info message to close it.
         * @param {MouseEvent} event - The mouse event triggered on click.
         */
        const handleClickOutside = (event) => {
            if (infoMessageRef.current && !infoMessageRef.current.contains(event.target) && showInfoMessage) {
                setShowInfoMessage(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading..." }), children: _jsx(ContentButton, { onClick: () => onSelect(option), isSelected: option.selected, hideOverflow: false, children: _jsxs("div", { className: "relative flex w-full flex-row", children: [_jsx(FloatingPortal, { root: anchorRef.current, children: _jsx("div", { className: "font-denim z-50 rounded-xl bg-white p-4 text-left", style: {
                                display: showInfoMessage ? 'block' : 'none',
                                boxShadow: '3px 3px 10px 3px rgba(3,6,23,0.06)',
                                maxWidth: '300px',
                            }, ref: infoMessageRef, onClick: (e) => {
                                e.stopPropagation();
                            }, children: _jsx("span", { className: "font-[Inter] text-xs font-normal leading-tight", children: infoIconDialog }) }) }), _jsx("div", { className: "absolute h-0 min-w-[8rem]", ref: anchorRef, style: { right: '16px', bottom: 'calc(100% - 12px)' } }), infoIconDialog && (_jsx("div", { onClick: (e) => {
                            e.stopPropagation();
                            setShowInfoMessage((v) => !v);
                        }, className: "absolute right-4 top-4 z-0", children: _jsx(InfoIcon, { color: "var(--primary-on-surface-text)", size: 16 }) })), _jsx("div", { className: "justify-items-center-safe h-auto max-w-20 place-content-center overflow-hidden rounded-s-lg bg-white text-center", style: {
                            flex: 2,
                            // text align for safari
                            textAlign: '-webkit-center',
                        }, children: iconsForMaterial[option.material] }), _jsx("div", { style: { flex: 8 }, children: _jsxs("div", { className: "align-start flex flex-col gap-y-1 p-[16px]", children: [_jsx("div", { className: "text-text-primary xl:text-text-primary flex flex-row justify-between pb-1 text-sm font-medium leading-none xl:text-base xl:font-medium xl:leading-tight", children: MaterialLabels[option.material] }), _jsxs("div", { className: "flex flex-col gap-y-2 text-left text-xs font-normal", children: [_jsx("div", { className: "text-text-primary justify-start self-stretch leading-normal", children: MaterialDescriptions[option.material] }), _jsx("div", { className: "text-text-secondary justify-start self-stretch italic leading-normal", children: MaterialDisclaimer[option.material] })] })] }) })] }) }) }));
};
//# sourceMappingURL=material-card.js.map